<template>
    <div class="row-start-2 flex">
        <button
            type="button"
            class="p-2 my-3 bg-dark-blue row-start-1 items-center justify-self-end self-center flex shadow-sm cursor-pointer text-white font-nexa text-sm rounded"
            @click="open"
        >
            Excel Data File
        </button>

        <!-- Modal -->
        <teleport to="body">
            <div
                v-if="localIsOpen"
                class="fixed inset-0 z-50 flex items-center justify-center"
                aria-live="assertive"
            >
                <!-- Backdrop -->
                <div class="absolute inset-0 bg-black/40" @click="close" />

                <!-- Dialog -->
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-label="Choose CSV download"
                    class="relative z-10 w-[22rem] max-w-[90vw] rounded-xl bg-white p-6 shadow-xl"
                >
                    <h2 class="mb-3 text-base font-semibold text-gray-800 font-nexa">
                        Data files
                    </h2>

                    <div class="flex flex-col">
                        <!-- Store -->
                        <button
                            type="button"
                            class="p-2 my-2 flex items-center justify-center shadow-sm cursor-pointer text-white font-nexa text-sm rounded focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            :class="{'bg-gray-400 cursor-not-allowed': is_loading, 'bg-dark-blue hover:opacity-90': !is_loading}"
                            :disabled="is_loading"
                            @click="store"
                        >
                            <span v-if="is_loading" class="loader mr-2"></span>
                            <span>{{ is_loading ? 'Creating...' : 'Create Excel data file' }}</span>
                        </button>

                        <!-- Latest 5 files -->
                        <template v-if="topFiveFiles.length">
                            <button
                                v-for="file in topFiveFiles"
                                :key="file.id ?? file.url ?? file.label"
                                type="button"
                                class="p-2 my-1 bg-dark-blue/90 flex items-center justify-between shadow-sm cursor-pointer text-white font-nexa text-sm rounded hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                @click="downloadFile(file)"
                            >
                                <span class="truncate text-left">
                                  Download {{ file.label }}
                                </span>
                            </button>
                        </template>

                        <!-- Empty state -->
                        <p v-else class="mt-2 text-sm text-gray-600">
                            No recent files found.
                        </p>
                    </div>

                    <button
                        type="button"
                        class="mt-4 ml-auto block text-sm text-gray-500 hover:text-gray-700"
                        @click="close"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </teleport>
    </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, computed, watch } from 'vue';
import { defineComponent } from 'vue';

export default defineComponent({

    props: {
        isOpen: {
            type: Boolean,
            default: false,
        },
        latestFiles: {
            type: Array,
            default: () => [],
        },
        fetch_url: {
            type: String,
            default: '',
        },
        store_url: {
            type: String,
            default: '/exports/newsletters/store',
        },
        files: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {

            is_loading: false,
        }
    },

    setup(props) {
        const localIsOpen = ref(props.isOpen);
        const localFiles = ref([...props.files]);

        watch(() => props.files, (newVal) => {
            localFiles.value = [...newVal];
        });

        return {
            localIsOpen,
            localFiles,
        };
    },

    computed: {
        topFiveFiles() {
            return this.localFiles.slice(0, 5);
        }
    },
    methods: {

        async fetch_files() {
            if (this.fetch_url) {
                try {
                    const res = await fetch(this.fetch_url, { credentials: 'same-origin' });
                    const data = await res.json();
                    this.localFiles = this.mapApiToFiles(data);
                } catch (e) {
                    console.log(e);
                }
            }
        },

        open() {
            this.localIsOpen = true;
            this.fetch_files();
            this.check_if_store_is_running();
        },
        close() {
            this.localIsOpen = false;
        },

        onKeydown(e) {
            if (e.key === 'Escape') {
                this.close();
            }
        },

        check_if_store_is_running() {

            // Simple endpoint that just returns a boolean if a store process is running
            fetch('/exports/newsletters/store/running', { credentials: 'same-origin' })
                .then(res => res.json())
                .then(data => {
                    this.is_loading = data.is_running;
                    if ( this.is_loading ) {
                        setTimeout(() => {
                            this.check_if_store_is_running();
                        }, 2000);
                    } else {
                        this.fetch_files();
                    }
                })
                .catch(e => {
                    console.error(e);
                });

        },

        store() {
            this.is_loading = true;
            try {
                fetch(this.store_url, { credentials: 'same-origin' });
            } catch (e) {
                console.error(e);
            } finally {
                setTimeout(() => {
                    this.check_if_store_is_running();
                }, 2000);
            }
        },

        downloadFile(file) {
            if (file.url) {
                window.location.href = file.url;
            }
            this.close();
        },

        mapApiToFiles(apiData) {
            return (apiData || []).slice(0, 5).map((item) => ({
                id: item.id,
                label: item.filename ?? item.name ?? `File ${item.id}`,
                url: item.download_url ?? item.url,
            }));
        },
    },

    async mounted() {
        window.addEventListener('keydown', this.onKeydown);

    },

    unmounted() {
        window.removeEventListener('keydown', this.onKeydown);
    }
});
</script>

<style>
.loader {
    border: 2px solid #f3f3f3;
    border-top: 2px solid #3498db;
    border-radius: 50%;
    width: 12px;
    height: 12px;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}
</style>
